import React, { useState } from 'react';
import { X, Users, UserMinus, AlertTriangle, User } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { huntService } from '@/services/api/huntService';

/**
 * Google "G" logo as an SVG icon
 */
const GoogleIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

interface Participant {
  odifiedUserId: string;
  displayName: string;
  role: 'hunter' | 'hider' | 'spectator';
  connectionStatus?: 'connected' | 'weak' | 'disconnected';
  isGuest?: boolean;
}

interface ParticipantDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  huntId: string;
  participants: Participant[];
  currentUserId: string;
  creatorId: string;
  onParticipantKicked?: (userId: string) => void;
}

interface KickModalProps {
  participant: Participant;
  onConfirm: (justification: string) => Promise<void>;
  onCancel: () => void;
  isKicking: boolean;
}

/**
 * Modal for confirming and justifying a participant kick
 */
const KickConfirmModal: React.FC<KickModalProps> = ({
  participant,
  onConfirm,
  onCancel,
  isKicking,
}) => {
  const [justification, setJustification] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (justification.trim().length < 5) {
      setError('Please provide a reason (at least 5 characters)');
      return;
    }
    if (justification.length > 200) {
      setError('Reason too long (max 200 characters)');
      return;
    }
    setError(null);
    await onConfirm(justification.trim());
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60] p-4 pb-safe-bottom">
      <div className="bg-secondary rounded-xl w-full max-w-md p-6 animate-slide-up max-h-[85vh] overflow-y-auto">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
            <AlertTriangle className="w-5 h-5 text-red-500" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">Remove Participant</h2>
            <p className="text-sm text-text-secondary">
              Kick {participant.displayName} from the hunt?
            </p>
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-sm text-text-secondary mb-2">
            Reason for removal <span className="text-red-400">*</span>
          </label>
          <textarea
            value={justification}
            onChange={(e) => setJustification(e.target.value)}
            placeholder="e.g., Inappropriate behavior, not following rules..."
            className="w-full px-4 py-3 bg-background rounded-lg border border-white/10 
                       text-white placeholder-text-secondary resize-none
                       focus:outline-none focus:border-primary/50"
            rows={3}
            maxLength={200}
          />
          <div className="flex justify-between mt-1">
            {error && <span className="text-xs text-red-400">{error}</span>}
            <span className="text-xs text-text-secondary ml-auto">
              {justification.length}/200
            </span>
          </div>
          
          {/* Quick fill button */}
          <button
            type="button"
            onClick={() => setJustification('*** Inactive Participant ***')}
            className="mt-2 px-3 py-1.5 text-xs bg-yellow-500/20 text-yellow-400 
                       rounded-lg hover:bg-yellow-500/30 transition-colors"
          >
            Quick: Inactive Participant
          </button>
        </div>

        <div className="flex gap-3">
          <Button
            variant="secondary"
            onClick={onCancel}
            className="flex-1"
            disabled={isKicking}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isKicking || justification.trim().length < 5}
            className="flex-1 bg-red-600 hover:bg-red-700"
          >
            {isKicking ? 'Removing...' : 'Remove'}
          </Button>
        </div>
      </div>
    </div>
  );
};

/**
 * Side drawer for managing hunt participants
 * Allows the hunt creator to view and kick participants
 */
export const ParticipantDrawer: React.FC<ParticipantDrawerProps> = ({
  isOpen,
  onClose,
  huntId,
  participants,
  currentUserId,
  creatorId,
  onParticipantKicked,
}) => {
  const [kickingParticipant, setKickingParticipant] = useState<Participant | null>(null);
  const [isKicking, setIsKicking] = useState(false);
  const [kickError, setKickError] = useState<string | null>(null);

  const isCreator = currentUserId === creatorId;

  const handleKickClick = (participant: Participant) => {
    setKickingParticipant(participant);
    setKickError(null);
  };

  const handleKickConfirm = async (justification: string) => {
    if (!kickingParticipant) return;

    setIsKicking(true);
    setKickError(null);

    try {
      await huntService.kickParticipant(huntId, kickingParticipant.odifiedUserId, justification);
      onParticipantKicked?.(kickingParticipant.odifiedUserId);
      setKickingParticipant(null);
    } catch (error) {
      setKickError(error instanceof Error ? error.message : 'Failed to remove participant');
    } finally {
      setIsKicking(false);
    }
  };

  const handleKickCancel = () => {
    setKickingParticipant(null);
    setKickError(null);
  };

  // Group participants by role
  const hunters = participants.filter((p) => p.role === 'hunter');
  const hiders = participants.filter((p) => p.role === 'hider');

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/60 z-40 transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      />

      {/* Drawer */}
      <div
        className={`fixed top-0 right-0 h-full w-80 max-w-[85vw] bg-secondary z-50 
                    transform transition-transform duration-300 ease-out shadow-2xl
                    ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <Users className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-semibold">Participants</h2>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-background/50 flex items-center justify-center
                       hover:bg-background transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto h-[calc(100%-4rem)]">
          {/* Error message */}
          {kickError && (
            <div className="mb-4 p-3 bg-red-500/20 border border-red-500/30 rounded-lg text-sm text-red-400">
              {kickError}
            </div>
          )}

          {/* Hunters Section */}
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-3 h-3 rounded-full bg-red-500" />
              <h3 className="text-sm font-medium text-text-secondary uppercase tracking-wide">
                Hunters ({hunters.length})
              </h3>
            </div>
            {hunters.length === 0 ? (
              <p className="text-sm text-text-secondary italic">No hunters yet</p>
            ) : (
              <div className="space-y-2">
                {hunters.map((participant) => (
                  <ParticipantCard
                    key={participant.odifiedUserId}
                    participant={participant}
                    isCreator={isCreator}
                    isSelf={participant.odifiedUserId === currentUserId}
                    onKick={() => handleKickClick(participant)}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Hiders Section */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-3 h-3 rounded-full bg-green-500" />
              <h3 className="text-sm font-medium text-text-secondary uppercase tracking-wide">
                Hiders ({hiders.length})
              </h3>
            </div>
            {hiders.length === 0 ? (
              <p className="text-sm text-text-secondary italic">No hiders yet</p>
            ) : (
              <div className="space-y-2">
                {hiders.map((participant) => (
                  <ParticipantCard
                    key={participant.odifiedUserId}
                    participant={participant}
                    isCreator={isCreator}
                    isSelf={participant.odifiedUserId === currentUserId}
                    isHuntCreator={participant.odifiedUserId === creatorId}
                    onKick={() => handleKickClick(participant)}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Creator Info */}
          {isCreator && (
            <div className="mt-6 p-3 bg-primary/10 border border-primary/20 rounded-lg">
              <p className="text-xs text-text-secondary">
                As the hunt creator, you can remove participants by tapping the remove button next to their name.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Kick confirmation modal */}
      {kickingParticipant && (
        <KickConfirmModal
          participant={kickingParticipant}
          onConfirm={handleKickConfirm}
          onCancel={handleKickCancel}
          isKicking={isKicking}
        />
      )}
    </>
  );
};

interface ParticipantCardProps {
  participant: Participant;
  isCreator: boolean;
  isSelf: boolean;
  isHuntCreator?: boolean;
  onKick: () => void;
}

const ParticipantCard: React.FC<ParticipantCardProps> = ({
  participant,
  isCreator,
  isSelf,
  isHuntCreator,
  onKick,
}) => {
  const connectionColor =
    participant.connectionStatus === 'connected'
      ? 'bg-green-500'
      : participant.connectionStatus === 'weak'
      ? 'bg-yellow-500'
      : 'bg-gray-500';

  return (
    <div className="flex items-center gap-3 p-3 bg-background rounded-lg">
      <div className="relative flex-shrink-0">
        <div
          className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium
                      ${participant.role === 'hunter' ? 'bg-red-500/20 text-red-400' : 'bg-green-500/20 text-green-400'}`}
        >
          {participant.displayName.charAt(0).toUpperCase()}
        </div>
        {participant.connectionStatus && (
          <div
            className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-background ${connectionColor}`}
          />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          {/* Auth type icon - default to guest if isGuest is undefined */}
          {participant.isGuest === false ? (
            <span title="Google Account" className="flex-shrink-0">
              <GoogleIcon className="w-3.5 h-3.5" />
            </span>
          ) : (
            <span title="Guest" className="flex-shrink-0">
              <User className="w-3.5 h-3.5 text-text-secondary" />
            </span>
          )}
          <span className="font-medium truncate">{participant.displayName}</span>
          {isSelf && (
            <span className="flex-shrink-0 text-xs px-1.5 py-0.5 bg-primary/20 text-primary rounded">You</span>
          )}
          {isHuntCreator && (
            <span className="flex-shrink-0 text-xs px-1.5 py-0.5 bg-yellow-500/20 text-yellow-400 rounded">
              Creator
            </span>
          )}
        </div>
        <span className="text-xs text-text-secondary capitalize">{participant.role}</span>
      </div>
      {isCreator && !isSelf && !isHuntCreator && (
        <button
          onClick={onKick}
          className="flex-shrink-0 w-8 h-8 rounded-full bg-red-500/10 flex items-center justify-center
                     text-red-400 hover:bg-red-500/20 transition-colors"
          title="Remove participant"
        >
          <UserMinus className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

export default ParticipantDrawer;
